import { EntityName } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback, useEffect, useRef } from 'react';
import { UPDATE_ENTITY_VIEWER_MUTATION } from 'src/graphql/entities/entity-viewer/entity-viewer.operations';
import {
  EntityViewerStatus,
  UpdateEntityViewerVars,
} from 'src/graphql/entities/entity-viewer/entity-viewer.types';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { useBroadcastChannelLeaderTab } from 'src/utils/hooks';

const HEARTBEAT_MS = 5 * 60_000; // 5 min
const JITTER = 0.15; // ±15%

export function useUpdateEntityViewer(params: {
  entity: EntityName;
  entityId?: string;
  status: EntityViewerStatus;
}) {
  const { user } = useCurrentUserProfile();
  const { entity, entityId } = params;
  const enabled = !!user && !!entity && !!entityId;
  const leaderScope = enabled
    ? `entity-viewer:${user.userId ?? user.id}:${entity}:${entityId}`
    : '';

  const isLeaderTab = useBroadcastChannelLeaderTab(leaderScope, enabled);

  const [mutate] = useMutation<
    { updateEntityViewer: { success: boolean } },
    UpdateEntityViewerVars
  >(UPDATE_ENTITY_VIEWER_MUTATION);

  const timerRef = useRef<number | null>(null);
  const runningRef = useRef(false);
  const lastInactiveRef = useRef(0);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const send = useCallback(
    async (status: EntityViewerStatus) => {
      if (!isLeaderTab) return;
      if (!user || !entity || !entityId) return;
      try {
        await mutate({
          variables: {
            params: {
              entity,
              entityId,
              status,
              partnerId: user.partnerId,
              firstName: user.firstName,
              lastName: user.lastName,
            },
          },
        });
      } catch {
        // ignore; next tick or resume will retry
      }
    },
    [entity, entityId, isLeaderTab, mutate, user],
  );

  const scheduleNext = useCallback(() => {
    clearTimer();
    if (!runningRef.current) return;
    const base = HEARTBEAT_MS;
    const jitter = base * (Math.random() * 2 * JITTER - JITTER);
    const delay = Math.max(10_000, base + jitter);
    timerRef.current = window.setTimeout(async () => {
      await send(EntityViewerStatus.active);
      scheduleNext();
    }, delay) as unknown as number;
  }, [send]);

  useEffect(() => {
    const start = () => {
      if (runningRef.current) return;
      runningRef.current = true;
      clearTimer();
      void send(EntityViewerStatus.active); // <-- immediate re-mark as active
      scheduleNext();
    };

    const stop = () => {
      runningRef.current = false;
      clearTimer();
    };

    const markInactiveOnce = async () => {
      const now = Date.now();
      if (now - lastInactiveRef.current < 1000) return; // dedupe
      lastInactiveRef.current = now;
      await send(EntityViewerStatus.inactive);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else {
        void markInactiveOnce();
        stop();
      }
    };

    const onOnline = () => {
      if (document.visibilityState === 'visible') start();
    };

    const onOffline = () => {
      // cannot mark inactive reliably; server should TTL presence
      stop();
    };

    const onPageHide = () => {
      void markInactiveOnce();
      stop();
    };

    // initial
    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'visible' &&
      (typeof navigator === 'undefined' || navigator.onLine)
    ) {
      start();
    }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('pagehide', onPageHide);
      void markInactiveOnce();
      stop();
    };
  }, [scheduleNext, send]);

  // optional manual ping
  const updateEntityViewer = useCallback(
    async (status?: EntityViewerStatus.active | EntityViewerStatus.inactive) => {
      await send(status ?? EntityViewerStatus.active);
    },
    [send],
  );

  return { updateEntityViewer };
}
